// frontend/src/pages/TopicsPage.jsx
import React, { useState, useEffect } from 'react';
import { getTopics, createTopic, updateTopic, deleteTopic, discoverRssFeeds, addRssFeed, removeRssFeed } from '../services/api';
import { Plus, Edit3, Trash2, Save, Loader, Tag, X, Search, Rss, Check, AlertCircle } from 'lucide-react';

const TagInput = ({ tags, onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const addTag = (value) => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const newTags = text.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    const unique = newTags.filter(t => !tags.includes(t));
    if (unique.length > 0) {
      onChange([...tags, ...unique]);
    }
  };

  return (
    <div className="w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-brand-mid p-2 min-h-[42px]">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-light text-brand-dark rounded text-xs">
            {tag}
            <button onClick={() => removeTag(i)} className="hover:text-red-500 transition-colors" type="button">
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => { if (input.trim()) addTag(input); }}
          className="flex-1 min-w-[120px] outline-none text-sm py-0.5"
          placeholder={tags.length === 0 ? placeholder : 'Type and press Enter...'}
        />
      </div>
    </div>
  );
};

const RssDiscoveryPanel = ({ topic, onFeedAdded }) => {
  const [domain, setDomain] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  const handleDiscover = async () => {
    if (!domain.trim()) return;
    setDiscovering(true);
    setResult(null);
    setError('');
    try {
      const res = await discoverRssFeeds(domain.trim());
      setResult(res.data);
    } catch (err) {
      setError('Failed to check domain for RSS feeds');
    } finally {
      setDiscovering(false);
    }
  };

  const handleAddFeed = async (feedUrl) => {
    setAdding(true);
    try {
      await addRssFeed(topic.id, feedUrl);
      setResult(null);
      setDomain('');
      onFeedAdded();
    } catch (err) {
      setError('Failed to add RSS feed');
    } finally {
      setAdding(false);
    }
  };

  const existingFeeds = topic.rss_feeds || [];
  const alreadyMonitored = result?.feed && existingFeeds.some(
    f => f.replace(/\/$/, '') === result.feed.feed_url.replace(/\/$/, '')
  );

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
        <Search size={16} />
        Check a website for RSS feed
      </h4>

      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleDiscover()}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
          placeholder="Enter a domain, e.g. youngminds.org.uk"
        />
        <button
          onClick={handleDiscover}
          disabled={discovering || !domain.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid disabled:opacity-50 text-sm"
        >
          {discovering ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
          Check
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm mb-3">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {result && !result.has_feed && (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <AlertCircle size={14} />
          No RSS feed found for {result.domain}
        </div>
      )}

      {result && result.has_feed && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Rss size={14} className="text-orange-500" />
              <span className="text-sm font-medium text-gray-800">{result.feed.feed_title}</span>
            </div>
            {alreadyMonitored ? (
              <span className="flex items-center gap-1 text-xs text-green-600 px-2 py-1 bg-green-50 rounded">
                <Check size={12} />
                Already monitored
              </span>
            ) : (
              <button
                onClick={() => handleAddFeed(result.feed.feed_url)}
                disabled={adding}
                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-brand-dark text-white rounded hover:bg-brand-mid disabled:opacity-50"
              >
                {adding ? <Loader size={12} className="animate-spin" /> : <Plus size={12} />}
                Add to {topic.name}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-2">{result.feed.feed_url}</p>
          <p className="text-xs text-gray-500 mb-1">Recent articles:</p>
          <div className="space-y-0.5">
            {result.feed.recent_items.slice(0, 3).map((item, i) => (
              <p key={i} className="text-xs text-gray-500 truncate">• {item.title}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TopicsPage = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRssPanel, setShowRssPanel] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    keywords: '',
    source_urls: '',
    relevance_terms: [],
    custom_prompts: { linkedin_post: '', white_paper: '', report: '', article: '', policy_brief: '' },
    is_active: true
  });

  useEffect(() => { loadTopics(); }, []);

  const loadTopics = async () => {
    try {
      const res = await getTopics();
      setTopics(res.data);
    } catch (err) {
      console.error('Failed to load topics', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', description: '', keywords: '', source_urls: '', relevance_terms: [],
      custom_prompts: { linkedin_post: '', white_paper: '', report: '', article: '', policy_brief: '' },
      is_active: true
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (topic) => {
    setForm({
      name: topic.name,
      description: topic.description || '',
      keywords: (topic.keywords || []).join(', '),
      source_urls: (topic.source_urls || []).join(', '),
      relevance_terms: topic.relevance_terms || [],
      custom_prompts: topic.custom_prompts || { linkedin_post: '', white_paper: '', report: '', article: '', policy_brief: '' },
      is_active: topic.is_active
    });
    setEditing(topic.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const data = {
      name: form.name,
      description: form.description,
      keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
      source_urls: form.source_urls.split(',').map((u) => u.trim()).filter(Boolean),
      relevance_terms: form.relevance_terms,
      custom_prompts: form.custom_prompts,
      is_active: form.is_active
    };
    try {
      if (editing) { await updateTopic(editing, data); }
      else { await createTopic(data); }
      resetForm();
      loadTopics();
    } catch (err) {
      console.error('Failed to save topic', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;
    try {
      await deleteTopic(id);
      setTopics(topics.filter((t) => t.id !== id));
    } catch (err) {
      console.error('Failed to delete topic', err);
    }
  };

  const handleRemoveRssFeed = async (topicId, feedUrl) => {
    try {
      await removeRssFeed(topicId, feedUrl);
      loadTopics();
    } catch (err) {
      console.error('Failed to remove RSS feed', err);
    }
  };

  const contentTypes = [
    { key: 'linkedin_post', label: 'LinkedIn Post' },
    { key: 'white_paper', label: 'White Paper' },
    { key: 'report', label: 'Report' },
    { key: 'article', label: 'Article' },
    { key: 'policy_brief', label: 'Policy Brief' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Topics</h1>
          <p className="text-gray-500 mt-1">Manage research topics and their configurations</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid transition-colors"
        >
          <Plus size={18} />
          Add Topic
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-brand-dark mb-4">
            {editing ? 'Edit Topic' : 'New Topic'}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                  placeholder="e.g., Youth Mental Health" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                rows={3} placeholder="Context for the AI when generating content about this topic..." />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Keywords (comma-separated)</label>
              <p className="text-xs text-gray-400 mb-1">Used to search for articles via Google News. Keep concise — 4-8 keyword phrases.</p>
              <input type="text" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                placeholder="youth mental health UK, CAMHS, adolescent wellbeing" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Relevance Terms</label>
              <p className="text-xs text-gray-400 mb-1">Used to score and rank articles by relevance. Type a term and press Enter, or paste a comma-separated list.</p>
              <TagInput tags={form.relevance_terms} onChange={(terms) => setForm({ ...form, relevance_terms: terms })}
                placeholder="e.g. neet, youth, apprenticeship, skills, mental health..." />
              <p className="text-xs text-gray-400 mt-1">{form.relevance_terms.length} terms configured</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source URLs (comma-separated domains)</label>
              <input type="text" value={form.source_urls} onChange={(e) => setForm({ ...form, source_urls: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                placeholder="theguardian.com, bbc.co.uk, youngminds.org.uk" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Prompts (per content type)</label>
              <div className="space-y-3">
                {contentTypes.map((ct) => (
                  <div key={ct.key}>
                    <label className="block text-xs text-gray-500 mb-1">{ct.label}</label>
                    <textarea value={form.custom_prompts[ct.key] || ''}
                      onChange={(e) => setForm({ ...form, custom_prompts: { ...form.custom_prompts, [ct.key]: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                      rows={2} placeholder={`Specific instructions for ${ct.label} generation...`} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSubmit} className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid">
              <Save size={16} />
              {editing ? 'Update Topic' : 'Create Topic'}
            </button>
          </div>
        </div>
      )}

      {topics.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Tag size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No topics yet. Add your first research topic.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {topics.map((topic) => (
            <div key={topic.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-md font-semibold text-gray-800">{topic.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${topic.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {topic.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{topic.description}</p>
                  <div className="mb-2">
                    <span className="text-xs font-medium text-gray-500 mr-2">Keywords:</span>
                    <div className="inline-flex flex-wrap gap-1.5">
                      {(topic.keywords || []).map((kw, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{kw}</span>
                      ))}
                    </div>
                  </div>
                  <div className="mb-2">
                    <span className="text-xs font-medium text-gray-500 mr-2">Relevance terms:</span>
                    <span className="text-xs text-gray-400">{(topic.relevance_terms || []).length} configured</span>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 mr-2">RSS feeds:</span>
                    <span className="text-xs text-gray-400">{(topic.rss_feeds || []).length} monitored</span>
                    <button
                      onClick={() => setShowRssPanel(showRssPanel === topic.id ? null : topic.id)}
                      className="text-xs text-brand-mid hover:text-brand-dark ml-2"
                    >
                      {showRssPanel === topic.id ? 'Hide' : 'Manage'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => handleEdit(topic)} className="p-2 text-gray-400 hover:text-brand-mid transition-colors" title="Edit">
                    <Edit3 size={18} />
                  </button>
                  <button onClick={() => handleDelete(topic.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {showRssPanel === topic.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                  {(topic.rss_feeds || []).length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Monitored feeds:</p>
                      <div className="space-y-1">
                        {(topic.rss_feeds || []).map((feed, i) => {
                          const domain = (() => { try { return new URL(feed).hostname.replace('www.', ''); } catch { return feed; } })();
                          return (
                            <div key={i} className="flex items-center justify-between bg-white border border-gray-200 px-3 py-2 rounded-lg">
                              <div className="flex items-center gap-2 min-w-0">
                                <Rss size={12} className="text-orange-500 flex-shrink-0" />
                                <span className="text-sm text-gray-700 font-medium">{domain}</span>
                                <span className="text-xs text-gray-400 truncate">{feed}</span>
                              </div>
                              <button onClick={() => handleRemoveRssFeed(topic.id, feed)}
                                className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-2" title="Remove feed">
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <RssDiscoveryPanel topic={topic} onFeedAdded={loadTopics} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopicsPage;
